class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    const predictiveUrl = `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`;

    // ClickUp 14ypaj0ceuj — the predictive endpoint under-matches alphanumeric
    // model prefixes (q=MBF finds 1 of 6, q=EPF finds 1 of 15) while full search
    // returns the correct set for the same query. Ask both, and prefer the full
    // search product list whenever it finds more. Failure here is non-fatal:
    // if the second request errors the dropdown behaves exactly as before.
    const searchRoute = (typeof routes !== "undefined" && routes.search_url) || "/search";
    const fullSearchUrl = `${searchRoute}?q=${query}&type=product&options[prefix]=last&section_id=predictive-search-products`;

    Promise.all([
      fetch(predictiveUrl).then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      }),
      fetch(fullSearchUrl)
        .then((response) => (response.ok ? response.text() : null))
        .catch(() => null),
    ])
      .then(([predictiveText, fullSearchText]) => {
        const doc = new DOMParser().parseFromString(predictiveText, "text/html");
        const container = doc.querySelector("#shopify-section-predictive-search");

        if (fullSearchText) {
          try {
            this.mergeFullSearchProducts(container, fullSearchText, searchTerm);
          } catch (mergeError) {
            // Leave the predictive markup untouched rather than break the dropdown.
            console.warn("Predictive search: full-search merge skipped", mergeError);
          }
        }

        const resultsMarkup = container.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  // Swap the predictive product list for the full-search one, but only when
  // full search actually found more. Everything else in the dropdown
  // (Suggestions, collections, pages, articles) is left as the predictive
  // endpoint returned it — those resources are not affected by the bug.
  mergeFullSearchProducts(container, fullSearchText, searchTerm) {
    if (!container) return;

    const fullDoc = new DOMParser().parseFromString(fullSearchText, "text/html");
    const fullBlock = fullDoc.querySelector("#predictive-search-products-full");
    if (!fullBlock) return;

    const fullCount = parseInt(fullBlock.getAttribute("data-product-count") || "0", 10);
    const fullSection = fullBlock.querySelector(".predictive-search-results-group-section");
    if (!fullSection || !fullCount) return;

    const existingList = container.querySelector("#predictive-search-results-item-product-list");
    const existingCount = existingList ? existingList.children.length : 0;

    // Predictive already did as well or better — change nothing.
    if (fullCount <= existingCount) return;

    // Only intervene when the dropdown is UNDER-FILLED. If predictive already
    // returned a full page of products the customer sees a complete list either
    // way, and swapping it would churn good results for no visible gain (q=ATO
    // returns 10 from predictive and 182 from full search — same 10 on screen).
    // The failure this fixes is predictive collapsing to one or two hits, e.g.
    // q=MBF (1 of 6) and q=EPF (1 of 15).
    const dropdownLimit = parseInt(this.dataset.limit || "10", 10) || 10;
    if (existingCount >= dropdownLimit) return;

    if (existingList) {
      const existingSection = existingList.closest(".predictive-search-results-group-section");
      if (existingSection) {
        existingSection.replaceWith(fullSection);
      } else {
        existingList.replaceWith(fullSection);
      }
    } else {
      // Predictive returned no products at all: add the group and restore the
      // two-column, non-empty layout the section would have rendered itself.
      const groups = container.querySelectorAll(".predictive-search-results-group");
      const target = groups.length ? groups[groups.length - 1] : null;
      if (!target) return;
      target.prepend(fullSection);

      const wrapper = container.querySelector(".predictive-search-results-group-wrapper");
      if (wrapper) wrapper.classList.add("predictive-search-results-group-wrapper-two-column");

      const list = container.querySelector(".predictive-search-results-list");
      if (list) list.classList.remove("predictive-search-results-list-empty");

      const loadingState = container.querySelector(".predictive-search-loading-state");
      if (loadingState) loadingState.remove();
    }

    // Keep the screen-reader count honest — it is read out by setLiveRegionResults().
    const countElement = container.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    if (countElement) {
      countElement.textContent = `Search results for “${searchTerm}”: ${fullCount} ${
        fullCount === 1 ? "item" : "items"
      }`;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
